import { useAuthorization } from 'components/Authorization';
import { useSetTriggerValue } from 'hooks/business/useTrigger';
import { useEffect, useRef } from 'react';
import posthog from '@leyan/lytics';
import { list as triggerList } from 'services/api/trigger';
import { configFlag } from 'services/api/flagr';
import { useSetFlagConfigValue } from 'hooks/business/system/useFlagConfig';
import {
  getIsOrgDoneAuthTutorial,
  getRegionLanguageMap,
  orgConfig,
  transferedStoreList,
  whiteList,
} from 'services/api/admin';
// import { deleteStoresData } from 'services/db/sqlite';
import nimInst from 'services/nim';
import {
  useSetFollowedStoreListState,
  useSetReviewFollowedStoreListState,
} from 'hooks/business/conversation';
import { searchStoreList } from 'services/api/store';
import { MAX_SELECTED_LENGTH } from 'components/StoreSelector/hooks/useStoreSelector';
import { useSetOrgConfigStateValue } from 'hooks/business/useOrgConfig';
import { useMount, useRequest } from 'ahooks';
import { useSetOpenReturnStatus } from 'hooks/business/openReturnStatus';
import { openReturn } from 'services/api/return';
import { useSetIsFinishNewbieAuth } from 'hooks/business/user/useNewbieAuthGuide';
import { useSetupNewPermission } from 'hooks/business/useNewPermisson';
import AppConfig from 'services/config/app';
import { useBatchFlagr } from 'hooks/useBatchFlagr';
import { incentiveInfo } from 'views/Management/NewuserIncentive/service';
import { useSetIncentive } from 'views/Management/NewuserIncentive/state';
import { status } from 'services/api/aiReply';
import useShops from 'hooks/business/authShops/useShops';
import { useSetRegionLanguageMap } from 'hooks/business/system/useRegionLanguage';
import { useRefreshERP } from 'views/Management/ERP/state';
import { setTradeApiV2Enabled } from 'services/api/tradeApiV2Whitelist';

function useSetup() {
  const { authorized, payload } = useAuthorization<Oversea.Token.Payload>();

  const { orgId } = payload || {};

  useSetupNewPermission();

  const setTriggerValue = useSetTriggerValue();

  const setFlagConfigValue = useSetFlagConfigValue();

  const setFollowList = useSetFollowedStoreListState();
  const setReviewFollowList = useSetReviewFollowedStoreListState();

  const setOrgConfig = useSetOrgConfigStateValue();

  const setOpenReturnStatus = useSetOpenReturnStatus();

  const setIsFinishNewbieAuth = useSetIsFinishNewbieAuth();

  const setIncentive = useSetIncentive();

  const batchFlagr = useBatchFlagr();

  const { deleteStateData } = useShops();

  const setRegionLanguageMap = useSetRegionLanguageMap();

  const { refreshERP } = useRefreshERP();

  const refsCurrent = {
    setTriggerValue,
    setFlagConfigValue,
    setOrgConfig,
  };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  const { runAsync: pollingFlag, cancel: cancelPollingFlag } = useRequest(
    () => {
      if (!payload) return Promise.resolve();

      return configFlag(payload.orgId)
        .then((res) => {
          if (!res || res.version === AppConfig.value.version) {
            return Promise.reject(new Error('Nothing to do'));
          }
          return res;
        })
        .then((res) => {
          const {
            current: { setFlagConfigValue },
          } = refs;
          setFlagConfigValue(res);
        })
        .catch(() => {});
    },
    {
      // 15分钟更新: 900000
      pollingInterval: 900000,
      manual: true,
    },
  );

  useEffect(() => {
    if (authorized) {
      triggerList()
        .then((res) => {
          const {
            current: { setTriggerValue },
          } = refs;

          setTriggerValue(res);
        })
        .catch(() => {});
    }

    if (authorized) {
      searchStoreList({
        follower: '1',
        cursor: 0,
        page_size: MAX_SELECTED_LENGTH,
      })
        .then((res) => {
          if (res?.data?.data) {
            setFollowList(res.data.data.data);
          }
        })
        .catch(() => []);

      searchStoreList({
        follower: '1',
        cursor: 0,
        page_size: MAX_SELECTED_LENGTH,
        scene: 'VIEW_REVIEW',
      })
        .then((res) => {
          if (res?.data?.data) {
            setReviewFollowList(res.data.data.data);
          }
        })
        .catch(() => []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorized]);

  useEffect(() => {
    if (authorized && payload) {
      pollingFlag();
    }

    return () => {
      cancelPollingFlag();
    };
  }, [authorized, payload, pollingFlag, cancelPollingFlag]);

  useEffect(() => {
    if (!authorized) {
      return () => {};
    }

    const deleteTransferedStoresData = () => {
      transferedStoreList()
        .then((storeList) => {
          const storeIdList = storeList.map((storeId) => `${storeId}`);
          deleteStateData(storeIdList);
        })
        .catch(() => {});
    };

    deleteTransferedStoresData();

    return nimInst.on('sys:message:store_transfer', () => {
      deleteTransferedStoresData();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorized]);

  useEffect(() => {
    if (!authorized || !orgId) {
      return;
    }

    orgConfig()
      .then((orgConfig) => {
        const {
          current: { setOrgConfig },
        } = refs;

        setOrgConfig((preConfig) => {
          return {
            ...preConfig,
            ...orgConfig,
          };
        });
      })
      .catch(() => {});

    batchFlagr();

    posthog.capture('$general', {
      type: 'authorized',
    });
  }, [authorized, batchFlagr, orgId]);

  useMount(() => {
    openReturn()
      .then(({ open }) => open)
      .then(setOpenReturnStatus)
      .catch(() => {});
    getIsOrgDoneAuthTutorial()
      .then((data) => {
        setIsFinishNewbieAuth(!data?.data?.data?.isTutorialDone);
      })
      .catch(() => {});

    incentiveInfo()
      .then(setIncentive)
      .catch(() => {});

    whiteList()
      .then((whiteListConfig) => {
        setTradeApiV2Enabled(
          (whiteListConfig as General.WhiteListConfig & { TRADE_API_V2_ENABLE?: boolean })
            .TRADE_API_V2_ENABLE,
        );
        setOrgConfig((pre) => ({
          ...pre,
          whiteListConfig,
          whiteListConfigLoaded: true,
        }));
      })
      .catch(() => {
        setTradeApiV2Enabled(false);
        setOrgConfig((pre) => ({
          ...pre,
          whiteListConfigLoaded: true,
        }));
      });

    status()
      .then(({ enable }) => ({ qaEnable: enable === false }))
      .then((res) => {
        setOrgConfig((pre) => ({
          ...pre,
          ...res,
        }));
      })
      .catch(() => {});

    getRegionLanguageMap()
      .then(({ translationConfig = {} }) => {
        setRegionLanguageMap((pre) => ({
          ...pre,
          ...translationConfig,
        }));
      })
      .catch(() => {});

    refreshERP().catch(() => {});
  });
}

export default useSetup;
